import { Button, Card, cn, LiveCaption, VoiceOrb, type VoiceOrbState } from '@wundo/ui';
import type { VoiceAgentState, VoiceFailure } from '@/lib/roleplayCall.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeVoiceStageProps {
  personaName: string;
  /** Null until the call is up; the orb waits rather than pretending to listen. */
  agentState: VoiceAgentState | null;
  /** What went wrong, when something did. Each has its own way out. */
  failure?: VoiceFailure;
  /** How loud whoever has the floor is, `0`–`1`. Absent before the call is up. */
  level?: number;
  /** The line being spoken, or empty. */
  caption?: string;
  captionsVisible: boolean;
  /** The character could not be heard on the last turn. The call goes on. */
  voiceTrouble?: boolean;
  /** Offered only where trying again is the thing to do. */
  onRetry?: () => void;
}

function statusFor(agentState: VoiceAgentState | null, personaName: string): string {
  if (agentState === 'speaking') {
    return m.practice_session_status_speaking({ name: personaName });
  }
  if (agentState === 'thinking') {
    return m.practice_session_status_thinking();
  }
  if (agentState === 'listening') {
    return m.practice_session_status_listening();
  }
  return m.practice_session_status_connecting();
}

/*
 * Three failures, three different things to say.
 *
 * A blocked microphone is not a network problem and never becomes one by
 * waiting: the answer is in the browser's own settings, so the message names
 * them. A call that dropped was working a moment ago, so it says what happened
 * rather than asking anybody to check their cables.
 */
function failureMessage(failure: VoiceFailure): string {
  if (failure === 'blocked') {
    return m.practice_session_mic_blocked();
  }
  if (failure === 'dropped') {
    return m.practice_session_voice_dropped();
  }
  return m.practice_session_voice_failed();
}

const ORB_STATE: Record<VoiceAgentState, VoiceOrbState> = {
  listening: 'listening',
  thinking: 'thinking',
  speaking: 'speaking',
};

/*
 * The stage owns its column rather than asking for a height of its own: a
 * conversation with nothing to read is the one screen where the whole area is
 * the point, and a card floating in the middle of empty space reads as a page
 * that failed to finish loading.
 *
 * Clipped, because on a wide screen the column hands the card a height and
 * whatever does not fit used to be drawn straight through its bottom edge and
 * over the page.
 */
const STAGE = 'flex min-h-95 flex-1 flex-col items-center justify-center overflow-hidden';

/**
 * A spoken conversation, on screen.
 *
 * There is nothing to read and nothing to do here, which is the point — the
 * user is talking. The orb says something is alive, the line under it says who
 * has the floor, and the sentence under that is the last thing the character
 * said, for anyone who missed a word or cannot hear it at all.
 */
export function PracticeVoiceStage({
  personaName,
  agentState,
  failure,
  level,
  caption = '',
  captionsVisible,
  voiceTrouble = false,
  onRetry,
}: PracticeVoiceStageProps) {
  if (failure !== undefined) {
    return (
      <Card as='section' className={cn(STAGE, 'gap-4 px-5.5 py-10 text-center')}>
        <p role='alert' className='max-w-90 text-md text-ink-body'>
          {failureMessage(failure)}
        </p>
        {onRetry !== undefined && (
          <Button size='sm' onClick={onRetry}>
            {m.practice_session_voice_retry()}
          </Button>
        )}
      </Card>
    );
  }

  return (
    <Card as='section' className={cn(STAGE, 'gap-5.5 px-5.5 py-10')}>
      {/* The caption is what gives way when the card is short, never the orb. */}
      <VoiceOrb
        state={agentState === null ? 'connecting' : ORB_STATE[agentState]}
        level={level}
        size='responsive'
        className='shrink-0'
      />

      <p
        aria-live='polite'
        className='text-xs font-bold tracking-widest text-ink-muted uppercase'
      >
        {statusFor(agentState, personaName)}
      </p>

      {/*
       * A turn that could not be spoken takes the caption's place: it is the
       * answer to the silence the user is sitting in, and the last caption is
       * about a line they already heard.
       */}
      {voiceTrouble ? (
        <p role='alert' className='max-w-90 text-center text-md text-ink-body'>
          {m.practice_session_voice_glitch()}
        </p>
      ) : (
        captionsVisible &&
        caption !== '' && <LiveCaption text={caption} className='max-w-130' />
      )}
    </Card>
  );
}
